import { Button, CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import useTheme from '@mui/material/styles/useTheme';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { Field, FieldProps, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { DateTime } from 'luxon';
import React from 'react';
import { useSelector } from 'react-redux';
import { pushMessage } from 'redux/reducers/messages';
import { getUserPermissions, updateUserData, userSelector } from 'redux/reducers/user';
import { useAppDispatch } from 'redux/store';
import { yupSchemas } from 'utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PhoneInput from 'react-phone-number-input/input';

const ProfileDetailsForm = () => {
    const theme = useTheme();
    const { userData } = useSelector(userSelector);
    const dispatch = useAppDispatch();

    const renderPhoneVerificationStatus = (phoneValue: string) => {
        // Don't show anything if phone is empty
        if (!phoneValue || !phoneValue.trim()) {
            return null;
        }

        const isVerified = userData?.phone_verified_at !== null;

        if (isVerified) {
            return (
                <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}
                >
                    <Typography
                        variant="body2"
                        color="green"
                        sx={{
                            fontSize: '0.8rem',
                        }}
                    >
                        <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'top' }} />
                        Your phone number is verified.
                    </Typography>
                </Box>
            );
        } else {
            return (
                <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}
                >
                    <Typography
                        variant="body2"
                        color="warning.main"
                        sx={{
                            fontSize: '0.8rem',
                        }}
                    >
                        <WarningIcon
                            color="warning"
                            sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'top' }}
                        />
                        Phone unverified. You may log in using this phone number to verify it.
                    </Typography>
                </Box>
            );
        }
    };

    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                firstName: userData?.first_name || '',
                lastName: userData?.last_name || '',
                email: userData?.email || '',
                userName: userData?.user_name || '',
                phone: userData?.phone_national ? userData.phone_national.replace(/\s/g, '') : '',
                phoneVerifiedAt: userData?.phone_verified_at || null,
                birthDate: userData?.fields?.birth_date || '',
            }}
            validationSchema={yupSchemas.profile}
            onSubmit={async (
                { firstName, lastName, email, userName, phone, birthDate },
                { setErrors, setSubmitting },
            ) => {
                setSubmitting(true);
                const result = await dispatch(
                    updateUserData({
                        userUuid: userData?.uuid || '',
                        first_name: firstName,
                        last_name: lastName,
                        email,
                        user_name: userName,
                        phone,
                        profile_photo: null,
                        fields: {
                            ...userData?.fields,
                            birth_date: birthDate,
                        },
                    }),
                );
                setSubmitting(false);
                if (updateUserData.fulfilled.match(result)) {
                    // We need to update organization to make sure if there are any updates done to personal organization
                    // due to user's name change, we pull the latest org name
                    // That happens through permissions
                    dispatch(getUserPermissions({ id: userData?.uuid || '' }));

                    dispatch(
                        pushMessage({
                            status: 'success',
                            message: 'Profile updated successfully.',
                        }),
                    );
                } else {
                    if (result.payload) {
                        // Server sent validation errors
                        if ('errors' in result.payload) {
                            dispatch(
                                pushMessage({
                                    status: 'error',
                                    message: result.payload.message,
                                }),
                            );
                            setErrors(result.payload?.errors || {});
                        } else {
                            // No validation errors
                            // Something might be wrong on server side
                            dispatch(
                                pushMessage({
                                    status: 'error',
                                    message: 'Something went wrong, please try again',
                                }),
                            );
                        }
                    }
                }
            }}
        >
            {({ isSubmitting, isValid, dirty, values, setFieldValue, errors }) => (
                <Form>
                    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Field
                            sx={{ width: '48%', marginBottom: theme.spacing(10) }}
                            id={'firstName'}
                            component={TextField}
                            variant="filled"
                            name="firstName"
                            label="First Name"
                            style={{ justifyContent: 'flex-start' }}
                        />
                        <Field
                            sx={{ width: '48%', marginBottom: theme.spacing(10) }}
                            component={TextField}
                            variant="filled"
                            name="lastName"
                            label="Last Name"
                            style={{ justifyContent: 'flex-end' }}
                        />
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Field
                            sx={{ width: '48%', marginBottom: theme.spacing(10) }}
                            component={TextField}
                            variant="filled"
                            name="email"
                            type="email"
                            label="Email Address"
                        />
                        <DatePicker
                            label="Birth Date"
                            value={
                                values.birthDate
                                    ? DateTime.fromFormat(values.birthDate, 'yyyy-MM-dd')
                                    : null
                            }
                            onChange={(value): void => {
                                const formattedDate = value ? value.toFormat('yyyy-MM-dd') : '';
                                setFieldValue('birthDate', formattedDate);
                            }}
                            slotProps={{
                                textField: {
                                    variant: 'filled',
                                    error: Boolean(errors.birthDate),
                                    helperText: errors.birthDate as string,
                                    sx: { width: '48%', marginBottom: theme.spacing(10) },
                                },
                            }}
                        />
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Field name="phone">
                            {({ field, form, meta }: FieldProps) => (
                                <Box
                                    sx={{
                                        width: '48%',
                                        marginBottom: theme.spacing(10),
                                        '& input': {
                                            '&:focus': {
                                                borderBottom: `2px solid ${theme.palette.primary.main} !important`,
                                            },
                                        },
                                    }}
                                >
                                    <PhoneInput
                                        {...field}
                                        country="US"
                                        value={
                                            field.value ? `+1${field.value.replace(/\D/g, '')}` : ''
                                        }
                                        onChange={(value: string | undefined) => {
                                            // Format as (XXX)XXX-XXXX to match validation schema
                                            const digits = value
                                                ? value.replace(/\D/g, '').replace(/^1/, '')
                                                : '';
                                            if (digits.length === 10) {
                                                const formatted = `(${digits.slice(
                                                    0,
                                                    3,
                                                )})${digits.slice(3, 6)}-${digits.slice(6)}`;
                                                void form.setFieldValue('phone', formatted);
                                            } else {
                                                // For incomplete numbers, just store the digits
                                                void form.setFieldValue('phone', digits);
                                            }
                                        }}
                                        placeholder="Phone Number"
                                        style={{
                                            width: '100%',
                                            padding: '16.5px 14px',
                                            fontSize: '1rem',
                                            fontFamily: 'inherit',
                                            border: 'none',
                                            outline: 'none',
                                            borderBottom: `1px solid ${
                                                meta.touched && meta.error
                                                    ? theme.palette.error.main
                                                    : 'rgba(0, 0, 0, 0.42)'
                                            }`,
                                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                        }}
                                    />
                                    {meta.touched && meta.error && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ ml: 1.75, mt: 0.375, fontSize: '0.75rem' }}
                                        >
                                            {meta.error}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Field>
                        <Box
                            sx={{
                                width: '48%',
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: theme.spacing(10),
                                justifyContent: 'flex-start',
                            }}
                        >
                            {renderPhoneVerificationStatus(values.phone)}
                        </Box>
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Field
                            sx={{ width: '48%', marginBottom: theme.spacing(10) }}
                            component={TextField}
                            variant="filled"
                            name="userName"
                            label="Username"
                            helperText="Must be unique. Letters, numbers, and underscores only"
                        />
                        <Box sx={{ width: '48%' }} />
                    </Box>
                    {!isSubmitting && (
                        <Button
                            size="large"
                            type="submit"
                            color="primary"
                            variant="contained"
                            disabled={!(isValid && dirty)}
                            sx={{ width: '48%', marginBottom: theme.spacing(10) }}
                        >
                            Update Details{'  '}
                        </Button>
                    )}{' '}
                    {isSubmitting && (
                        <Button
                            size="large"
                            type="button"
                            color="primary"
                            variant="contained"
                            sx={{ width: '100%', marginBottom: theme.spacing(10) }}
                        >
                            Updating Profile{'  '}
                            <CircularProgress
                                disableShrink
                                size={35}
                                color="inherit"
                                sx={{ marginLeft: theme.spacing(5) }}
                            />
                        </Button>
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default ProfileDetailsForm;
